import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ScrollToTop from "@/components/feature/ScrollToTop";
import PageMeta from "@/components/feature/PageMeta";
import ArticleCard from "@/components/feature/ArticleCard";
import {
  getArticleBySlug,
  getCategoryBySlug,
  getRelatedArticles,
} from "@/mocks/data";

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticleBySlug(slug) : undefined;

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-20 text-center">
          <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-full bg-background-100">
            <i className="ri-file-search-line text-3xl text-foreground-400"></i>
          </div>
          <h1 className="text-xl font-semibold text-foreground-950 mb-3">
            文章未找到
          </h1>
          <p className="text-foreground-600 mb-6">
            您访问的文章不存在或已被移除
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-full font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            <i className="ri-arrow-left-line"></i>
            返回首页
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const category = getCategoryBySlug(article.categorySlug);
  const relatedArticles = getRelatedArticles(article, 3);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.excerpt,
    author: {
      "@type": "Person",
      name: article.author,
    },
    datePublished: article.publishDate,
    image: article.thumbnailUrl,
    publisher: {
      "@type": "Organization",
      name: "免费91网页",
      logo: {
        "@type": "ImageObject",
        url: "https://www.91web.com/logo.png",
      },
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `https://www.91web.com/article/${article.slug}`,
    },
  };

  return (
    <>
      <PageMeta
        title={`${article.title} - 免费91网页`}
        description={article.excerpt}
        keywords={`免费91网页,${article.title},${category?.name || ""},91网页免费入口,免费91网页在线访问`}
        canonicalUrl={`https://www.91web.com/article/${article.slug}`}
        ogType="article"
        ogImage={article.thumbnailUrl}
      />
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <ScrollToTop />

        <Breadcrumb
          items={[
            { label: "首页", href: "/" },
            {
              label: category?.name || "分类",
              href: `/category/${article.categorySlug}`,
            },
            { label: article.title },
          ]}
        />

        <main>
          {/* Article Header */}
          <article className="max-w-4xl mx-auto px-4 md:px-6 py-8">
            <header className="mb-8">
              {category && (
                <Link
                  to={`/category/${category.slug}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary-100 text-primary-700 text-xs font-medium mb-4 hover:bg-primary-200 transition-colors whitespace-nowrap"
                >
                  <i className={`${category.icon}`}></i>
                  {category.name}
                </Link>
              )}
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 leading-tight mb-4">
                {article.title}
              </h1>
              <div className="flex items-center gap-4 flex-wrap text-sm text-foreground-500">
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-line"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line"></i>
                  {article.publishDate}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-time-line"></i>
                  阅读时间 {article.readTime}
                </span>
              </div>
            </header>

            {/* Featured Image */}
            <div className="rounded-lg overflow-hidden mb-10">
              <img
                src={article.thumbnailUrl}
                alt={article.title}
                className="w-full h-auto object-cover"
              />
            </div>

            {/* Article Content */}
            <div
              className="prose prose-base max-w-none text-foreground-800 mb-12 leading-relaxed 
                [&>p]:mb-5 [&>p]:text-foreground-700 [&>p]:leading-relaxed
                [&>h2]:text-xl [&>h2]:font-bold [&>h2]:text-foreground-950 [&>h2]:mt-10 [&>h2]:mb-4
                [&>h3]:text-lg [&>h3]:font-semibold [&>h3]:text-foreground-900 [&>h3]:mt-8 [&>h3]:mb-3"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            {/* Tags / Share */}
            <div className="border-t border-background-200 pt-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm text-foreground-500">标签：</span>
                {category && (
                  <Link
                    to={`/category/${category.slug}`}
                    className="px-3 py-1 rounded-full bg-background-100 text-foreground-600 text-xs hover:bg-background-200/70 transition-colors"
                  >
                    {category.name}
                  </Link>
                )}
                <span className="px-3 py-1 rounded-full bg-background-100 text-foreground-600 text-xs">
                  免费91网页
                </span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm text-foreground-500">分享：</span>
                <button className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors">
                  <i className="ri-wechat-line"></i>
                </button>
                <button className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors">
                  <i className="ri-weibo-line"></i>
                </button>
                <button className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors">
                  <i className="ri-link"></i>
                </button>
              </div>
            </div>
          </article>

          {/* Related Articles */}
          {relatedArticles.length > 0 && (
            <section className="max-w-7xl mx-auto px-4 md:px-6 py-14">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-8">
                相关文章推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {relatedArticles.map((related) => (
                  <ArticleCard key={related.id} article={related} />
                ))}
              </div>
            </section>
          )}
        </main>

        <Footer />
      </div>
    </>
  );
}